import React from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router-dom'
import { NotificationBell } from '@revalize/notifications/react'
import { useAuth } from '@/hooks/useAppAuth'
import { LoginButton } from '@/features/auth/components/LoginButton'
import { UserMenuDropdown } from '@/components/layout/UserMenuDropdown'
import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { cn } from '@/lib/utils'

interface HeaderProps {
  logo?: React.ReactNode
  nav?: React.ReactNode
  className?: string
}

export const Header = ({ logo, nav, className }: HeaderProps): JSX.Element => {
  const { isAuthenticated, isLoading } = useAuth()

  return (
    // `relative z-50` is load-bearing, not decoration.
    //
    // `backdrop-blur` sets `backdrop-filter`, and that creates a STACKING
    // CONTEXT. Everything inside the header — including the user dropdown's
    // own `z-50` — is then confined to it, so that z-index competes only with
    // the header's siblings, never with page content.
    //
    // The header was also `position: static`, which paints below every
    // positioned element regardless of z-index. So ordinary canvas overlays at
    // `z-10` painted over the entire dropdown, and "Send diagnostics" could be
    // seen but not clicked — one of the two ways to report a problem, closed
    // by a blur effect.
    //
    // Positioning the header and lifting it above page content (which tops out
    // at `z-20`) fixes it. It stays below the modal dialogs at `z-[60]` and
    // `z-[100]`, which should cover the header.
    <header className={cn('relative z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60', className)}>
      <div className="px-6">
        <div className="flex h-14 items-center justify-between">
          <div className="flex items-center gap-6">
            {logo ?? (
              <Link to="/" className="font-semibold text-lg">
                App
              </Link>
            )}
            {isAuthenticated && nav && (
              <nav className="hidden md:flex items-center gap-1">
                {nav}
              </nav>
            )}
          </div>

          <div className="flex items-center gap-2">
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : isAuthenticated ? (
              <>
                <ThemeToggle />
                <NotificationBell />
                <UserMenuDropdown />
              </>
            ) : (
              <LoginButton size="sm" />
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
